export const PAYMENT_CHALLENGE_ACTION = 'payment'
export const PAYMENT_CHALLENGE_TIMEOUT_MESSAGE = 'Please complete the security check and try again.'

// Donor-safe failures from Turnstile (timeouts, domain not authorized). Other setup failures keep
// a generic message so raw fetch/JSON/Stripe text never reaches the payment errors element.
export class PaymentChallengeError extends Error {
  constructor(message, { fatal = false } = {}) {
    super(message)
    this.name = 'PaymentChallengeError'
    this.fatal = fatal
  }
}

export function paymentChallengeToken(root = document) {
  return root.querySelector('[name="cf-turnstile-response"]')?.value || ''
}

export function paymentChallengeEnabled(root = document) {
  return Boolean(root.querySelector('.cf-turnstile'))
}

function paymentChallengeWidget(root = document) {
  return root.querySelector('.cf-turnstile')
}

function paymentChallengeStoredError(root = document) {
  return paymentChallengeWidget(root)?.dataset?.turnstileError || ''
}

function clearPaymentChallengeStoredError(root = document) {
  const widget = paymentChallengeWidget(root)
  if (widget) delete widget.dataset.turnstileError
}

export function resetPaymentChallenge(root = document) {
  clearPaymentChallengeStoredError(root)
  const widget = paymentChallengeWidget(root)
  if (!widget || !window.turnstile) return

  const widgetId = widget.dataset.turnstileWidgetId
  widgetId ? window.turnstile.reset(widgetId) : window.turnstile.reset()
}

// Read the token and immediately reset the widget so a concurrent setup cannot reuse a single-use
// Turnstile token (siteverify rejects the second use and leaves the donor with no Payment Element).
export function takePaymentChallengeToken(root = document) {
  const token = paymentChallengeToken(root)
  if (token) resetPaymentChallenge(root)
  return token
}

export function storePaymentChallengeError(root, errorCode) {
  const widget = paymentChallengeWidget(root)
  if (!widget) return
  widget.dataset.turnstileError = String(errorCode || '')
}

// https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
// Configuration/browser failures Turnstile will not recover from (invalid sitekey, domain not
// authorized, invalid action/cData, unsupported browser). Not every 110xxx is fatal: 110600/110620
// (challenge/interaction timed out) recover on retry, so everything else is left to Turnstile.
const FATAL_PAYMENT_CHALLENGE_ERRORS = new Set(['110100', '110110', '110200', '110420', '110430', '110500', '110510'])

export function isFatalPaymentChallengeError(errorCode) {
  return FATAL_PAYMENT_CHALLENGE_ERRORS.has(String(errorCode || ''))
}

export function paymentChallengeErrorMessage(errorCode) {
  const code = String(errorCode || '')
  if (code === '110200') {
    return 'Payment security could not start on this site (domain not authorized). Please contact support.'
  }
  if (code) {
    return `Payment security check failed (${code}). Please refresh and try again.`
  }
  return 'Please complete the security check and try again.'
}

function paymentChallengeErrorFromCode(errorCode) {
  return new PaymentChallengeError(paymentChallengeErrorMessage(errorCode), {
    fatal: isFatalPaymentChallengeError(errorCode)
  })
}

export function waitForPaymentChallenge(root = document, { timeoutMs = 30000, signal } = {}) {
  if (!paymentChallengeEnabled(root)) return Promise.resolve('')

  // error-callback may have fired while the donor was still on an earlier wizard step — before
  // anyone was listening. Persist that on the widget and fail immediately here.
  const storedError = paymentChallengeStoredError(root)
  if (storedError) {
    return Promise.reject(paymentChallengeErrorFromCode(storedError))
  }

  const existing = paymentChallengeToken(root)
  if (existing) return Promise.resolve(existing)

  if (signal?.aborted) {
    return Promise.reject(new DOMException('Payment challenge wait aborted', 'AbortError'))
  }

  return new Promise((resolve, reject) => {
    let settled = false
    const finish = (error, token) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      clearInterval(poll)
      root.removeEventListener('payment-challenge:ready', onReady)
      root.removeEventListener('payment-challenge:error', onError)
      signal?.removeEventListener('abort', onAbort)
      if (error) reject(error)
      else resolve(token)
    }
    const onReady = (event) => {
      clearPaymentChallengeStoredError(root)
      const token = event.detail?.token || paymentChallengeToken(root)
      if (token) finish(null, token)
    }
    const onError = (event) => {
      const errorCode = event.detail?.errorCode
      if (!isFatalPaymentChallengeError(errorCode)) return
      finish(paymentChallengeErrorFromCode(errorCode))
    }
    const onAbort = () => {
      finish(new DOMException('Payment challenge wait aborted', 'AbortError'))
    }
    const timer = setTimeout(() => {
      resetPaymentChallenge(root)
      finish(new PaymentChallengeError(PAYMENT_CHALLENGE_TIMEOUT_MESSAGE))
    }, timeoutMs)
    const poll = setInterval(() => {
      const stored = paymentChallengeStoredError(root)
      if (stored && isFatalPaymentChallengeError(stored)) {
        finish(paymentChallengeErrorFromCode(stored))
        return
      }
      const token = paymentChallengeToken(root)
      if (token) finish(null, token)
    }, 200)
    root.addEventListener('payment-challenge:ready', onReady)
    root.addEventListener('payment-challenge:error', onError)
    signal?.addEventListener('abort', onAbort)
  })
}
